import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Bell, Sparkles, Clock, BarChart3, Shield, Layers,
    CheckCircle2, ArrowRight, Globe, ChevronDown, Database, FileSpreadsheet,
    X, Users, Zap, Building2, Lock, TrendingUp, Star,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PublicSeo from "@/components/PublicSeo";
import { buildHomeSchemas } from "@/lib/publicSeoSchemas";

const LOGO_URL = "/licitx-logo.png";
const INNOVAX_LOGO_URL = "https://innovax.cl/wp-content/uploads/2024/09/logo-dos.svg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.licitx.cl";
const API_BASE = BACKEND_URL;

/* -------------------------------------------------------------------------- */
/* Datos de planes                                                             */
/* -------------------------------------------------------------------------- */
const PLANS_DATA = [
    {
        key: "starter",
        name: "Starter",
        icon: Zap,
        users: "Hasta 3 usuarios",
        description: "Ideal para equipos pequeños que quieren empezar a ganar licitaciones públicas en Chile sin perder tiempo en el portal.",
        price_monthly_uf: 0.5,
        price_annual_uf: 5.0,
        price_monthly: 20255,
        price_annual: 202546,
        features: [
            "3 usuarios incluidos",
            "Reglas de priorización ilimitadas",
            "Sincronización cada 15 minutos",
            "Alertas in-app y por email",
            "Licitaciones favoritas con filtro",
            "Compartir licitaciones por email",
            "Módulo de documentos con líneas de negocio",
            "Sistema de tickets de soporte",
            "Exporta la información a XLSX y CSV",
            "Soporte por email",
        ],
        locked: [
            "Distribución por prioridad (dashboard)",
            "Reportería trimestral (Q)",
            "Módulo de clientes",
            "Propuesta Comercial con IA",
            "Análisis de mercado",
            "Oportunidades anticipadas (solo Enterprise)",
            "Análisis histórico de licitaciones (solo Enterprise)",
            "Mi Posicionamiento en Mercado Público (solo Enterprise)",
            "Asistente IA conversacional (solo Enterprise)",
            "Checklist post-adjudicación con IA (solo Enterprise)",
            "Integración MCP con Claude, Cursor y ChatGPT (solo Enterprise)",
        ],
        highlight: false,
    },
    {
        key: "business",
        name: "Business",
        icon: Users,
        users: "Hasta 10 usuarios",
        description: "Para empresas con equipos comerciales activos que postulan a múltiples licitaciones ChileCompra al mes.",
        price_monthly_uf: 1.3,
        price_annual_uf: 13.0,
        price_monthly: 52664,
        price_annual: 526643,
        features: [
            "10 usuarios incluidos",
            "Todo lo de Starter",
            "Distribución por prioridad (dashboard)",
            "Reportería trimestral (Q) con comparativas",
            "Módulo de clientes de adjudicadas",
            "Alertas automáticas de cierre (15/10/5 días)",
            "Dashboard ejecutivo avanzado",
            "Branding personalizado",
            "Auditoría completa de acciones",
            "Soporte prioritario",
        ],
        locked: [
            "Propuesta Comercial con IA",
            "Análisis de mercado",
            "Oportunidades anticipadas (solo Enterprise)",
            "Análisis histórico de licitaciones (solo Enterprise)",
            "Mi Posicionamiento en Mercado Público (solo Enterprise)",
            "Asistente IA conversacional (solo Enterprise)",
            "Checklist post-adjudicación con IA (solo Enterprise)",
            "Integración MCP con Claude, Cursor y ChatGPT (solo Enterprise)",
        ],
        highlight: true,
    },
    {
        key: "enterprise",
        name: "Enterprise",
        icon: Building2,
        users: "50+ usuarios",
        description: "Para grandes organizaciones con múltiples equipos, integración CRM/ERP y gestión de licitaciones a escala.",
        price_monthly_uf: 2.8,
        price_annual_uf: 28.0,
        price_monthly: 113426,
        price_annual: 1134260,
        features: [
            "50+ usuarios",
            "Todo lo de Business",
            "Propuesta Comercial con IA (Word personalizado)",
            "Análisis de mercado con IA: consultas ad-hoc descargables en Excel",
            "Oportunidades anticipadas: predicción de relicitaciones con alertas escalonadas",
            "Análisis histórico: competidores, adjudicatarios, montos y tendencias",
            "Mi Posicionamiento: adjudicaciones propias, evolución, oportunidades no atacadas",
            "Checklist post-adjudicación: documentos para firma de contrato generados por IA",
            "Multi-tenant por división",
            "Webhooks y API pública",
            "Integraciones CRM / ERP",
            "Alertas vía WhatsApp (configuración disponible)",
            "Reportes ejecutivos exportables (market share, top adjudicatarios, evolución)",
            "Asistente IA conversacional (análisis de bases, estrategia y referencias de precio)",
            "Integración MCP con Claude, Cursor y ChatGPT (OAuth seguro y solo lectura)",
            "SLA y soporte dedicado",
        ],
        locked: [],
        highlight: false,
    },
];

/* -------------------------------------------------------------------------- */
/* Marketing copy                                                              */
/* -------------------------------------------------------------------------- */
const FEATURES = [
    {
        icon: Clock,
        title: "Monitoreo ChileCompra cada 15 minutos",
        body: "Conectamos con la API oficial de ChileCompra vía sincronización automática cada 15 minutos. Tu equipo ve oportunidades de licitaciones públicas, Compra Ágil y Órdenes de Compra casi en tiempo real, sin pegarse al portal.",
    },
    {
        icon: Layers,
        title: "Reglas de priorización inteligente",
        body: "Define reglas Alta / Media / Baja combinando keywords, monto mínimo (CLP o UF), categoría ChileCompra y organismo. LicitX clasifica cada licitación al instante y asigna responsables automáticamente.",
    },
    {
        icon: Sparkles,
        title: "Análisis de bases con IA y generación de propuesta",
        body: "La IA analiza bases técnicas y administrativas, extrae requisitos obligatorios, criterios de evaluación, ponderaciones, plazos y condiciones económicas. Puedes adjuntar bases, aclaraciones, adendas y respuestas para generar un borrador Word con el branding y los antecedentes de tu empresa, listo para revisión del equipo.",
    },
    {
        icon: BarChart3,
        title: "Postulación y gestión del proceso completo",
        body: "Gestiona todo el proceso de postulación desde LicitX: marca licitaciones como 'en preparación', 'postulada', 'adjudicada' o 'desierta'; asigna responsables; agrega notas internas, documentos y fechas clave. El sistema registra cada acción en el log de auditoría, calcula tu tasa de adjudicación histórica y centraliza todo el proceso de postulación en un solo lugar para que tu equipo comercial opere con máxima eficiencia.",
    },
    {
        icon: FileSpreadsheet,
        title: "Documentos, OC y Compra Ágil centralizados",
        body: "Gestiona documentos corporativos con alertas de renovación, sincroniza Órdenes de Compra y Compra Ágil automáticamente. Todo el ciclo de venta al Estado en una sola plataforma, sin saltar entre portales.",
    },
    {
        icon: Shield,
        title: "Alertas multicanal: email, in-app y WhatsApp",
        body: "Recibe notificaciones instantáneas cuando se publica una licitación que coincide con tus reglas, cuando una licitación está a 15, 10 o 5 días de cerrar, o cuando un documento corporativo está próximo a vencer. Las alertas llegan por email vía SendGrid, notificaciones in-app en tiempo real y, en el plan Enterprise, alertas configurables vía WhatsApp para que ningún plazo se escape. Las alertas WhatsApp se configuran desde el panel de administración y permiten notificar a todo el equipo comercial en tiempo real: nuevas oportunidades detectadas, cierres urgentes y vencimientos de documentos, todo directamente en el WhatsApp de cada integrante del equipo.",
    },
    {
        icon: BarChart3,
        title: "Gestión integral del proceso de licitación",
        body: "LicitX cubre cada etapa del proceso licitatorio: detección automática, priorización, asignación de responsables, preparación de propuesta con IA, seguimiento por estados (en preparación → postulada → adjudicada → desierta), gestión de documentos con alertas de renovación, centralización de Órdenes de Compra y Compra Ágil, y reportería trimestral para tu comité comercial. Todo el ciclo de venta al Estado en una sola plataforma.",
    },
    {
        icon: TrendingUp,
        title: "Inteligencia y análisis de mercado",
        body: "Analiza precios históricos de adjudicaciones en ChileCompra para conocer cuánto han pagado distintos organismos por servicios o productos similares. Consulta market share por rubro y comprador, revisa Órdenes de Compra de proveedores de tu nicho y detecta organismos que tu empresa todavía no aborda. Usa esos antecedentes para construir una estrategia de precio basada en datos del mercado público chileno.",
    },
    {
        icon: Star,
        title: "Favoritos, colaboración y Score IA",
        body: "Marca licitaciones como favoritas, compártelas con el equipo y recibe alertas prioritarias. El Score IA combina historial del organismo, adjudicatarios anteriores, montos de procesos similares y el perfil de tu empresa para entregar una señal de ajuste comercial y un rango de precio de referencia. También muestra resultados históricos por rubro y organismo para priorizar con más contexto.",
    },
];

const STEPS = [
    {
        n: "01",
        title: "Configura tu empresa y equipo",
        body: "Sube tu logo, define la paleta de marca, carga tus documentos corporativos y crea las cuentas de tu equipo en minutos. El software se personaliza para reflejar tu identidad desde el primer día.",
    },
    {
        n: "02",
        title: "Define reglas y detecta oportunidades",
        body: "Indica qué licitaciones públicas te interesan por keywords, monto, categoría u organismo. Cada 15 minutos el sistema revisa ChileCompra, Compra Ágil y Órdenes de Compra y aplica tus criterios automáticamente.",
    },
    {
        n: "03",
        title: "Genera propuestas con IA y postula",
        body: "Con un clic, la IA lee íntegramente las bases extensas de la licitación, extrae requisitos y criterios de evaluación, y genera un borrador de propuesta comercial en Word usando tus documentos corporativos y el branding de tu empresa. Gestiona todo el proceso de postulación: edita, reformula con instrucciones de voz, adjunta archivos y postula directamente desde la plataforma.",
    },
    {
        n: "04",
        title: "Analiza, adjudica y mide resultados con IA",
        body: "Consulta el Score IA como señal de ajuste comercial, revisa rangos de precio históricos y analiza proveedores adjudicados en procesos similares. Registra el resultado de cada postulación, gestiona Órdenes de Compra y mide tu evolución por rubro y organismo. Enterprise suma alertas escalonadas y reportería ejecutiva para mantener documentado el ciclo de venta al Estado.",
    },
];

const BENEFITS = [
    "Score IA explicable: señal de ajuste por empresa, referencias de precio y antecedentes de proveedores adjudicados en procesos similares de ChileCompra.",
    "Reduce la revisión manual del portal y concentra al equipo comercial en evaluar, preparar y dar seguimiento a oportunidades relevantes.",
    "Gestión integral del proceso de licitación: desde la detección automática hasta la adjudicación, gestión de OC y reportería, todo en una sola plataforma.",
    "Analiza bases extensas con IA: extrae requisitos, criterios de evaluación y condiciones económicas, y permite consultar el PDF para estructurar un borrador de propuesta.",
    "Gestiona todo el proceso de postulación desde LicitX: estados, responsables, notas, documentos y log de auditoría completo por cada licitación.",
    "Alertas multicanal (email, in-app y WhatsApp en Enterprise) para que ningún plazo de licitación se escape a tu equipo.",
    "Detecta licitaciones, Compra Ágil y Órdenes de Compra que típicamente se escapan por el volumen del portal ChileCompra.",
    "Analiza precios históricos, market share por rubro y Órdenes de Compra de proveedores del mismo nicho para sustentar la estrategia comercial con datos del mercado público.",
    "Estandariza la respuesta de tu equipo con responsables, estados de seguimiento y log de auditoría completo por cada licitación.",
    "Sin instalación: la plataforma corre en la nube con asistente virtual integrado, accesible desde cualquier dispositivo.",
];

const DIFFERENTIATORS = [
    {
        icon: Layers,
        title: "Prioridad explicable, no coincidencias opacas",
        body: "Cada oportunidad indica qué regla, palabra clave, monto, organismo y región coincidieron. Las condiciones se combinan para que las prioridades Alta y Media respondan al foco comercial real de tu empresa.",
    },
    {
        icon: Clock,
        title: "Oportunidades anticipadas antes de la publicación",
        body: "Identifica contratos y procesos con señales de renovación entre 3 y 12 meses. Enterprise activa alertas escalonadas a 6, 5, 4, 3, 2 y 1 mes, además de 15 días.",
    },
    {
        icon: Zap,
        title: "Compra Ágil con contexto para decidir rápido",
        body: "Revisa región, fecha y hora de cierre, productos solicitados, cantidad de participantes, favoritos y prioridad por reglas desde una vista preparada para alto volumen.",
    },
    {
        icon: BarChart3,
        title: "Competidores, organismos y precios adjudicados",
        body: "Analiza licitaciones y Órdenes de Compra por nicho, proveedor y organismo. Compara montos, evolución, market share por RUT y oportunidades que tu empresa todavía no aborda.",
    },
    {
        icon: FileSpreadsheet,
        title: "De las bases a una propuesta reutilizable",
        body: "Extrae requisitos y criterios de evaluación, incorpora anexos y aclaraciones, y genera una propuesta Word con el branding y los documentos corporativos de tu empresa.",
    },
    {
        icon: Shield,
        title: "Gestión y datos preparados para equipos",
        body: "Coordina responsables, estados, documentos por línea de negocio, aprobaciones y post-adjudicación. Enterprise suma reportes gerenciales, archivos generados por IA y MCP de solo lectura.",
    },
];

const FAQ = [
    {
        q: "¿LicitX reemplaza a ChileCompra?",
        a: "No. ChileCompra (mercadopublico.cl) sigue siendo el portal oficial donde se publican las licitaciones. LicitX es la plataforma integral de gestión licitaciones ChileCompra que cubre todo el ciclo: monitoreo automático, priorización, alertas, generación de propuestas con IA, seguimiento de postulaciones, gestión documental, Compra Ágil y Órdenes de Compra. Tu equipo deja de revisar manualmente el portal y gestiona todo el proceso de venta al Estado desde LicitX.",
    },
    {
        q: "¿Cuánto cuestan los planes de LicitX?",
        a: "LicitX tiene tres planes expresados en UF + IVA, lo que protege el precio de la inflación. El plan Starter (hasta 3 usuarios) cuesta 0,5 UF/mes o 5 UF/año. El plan Business (hasta 10 usuarios) cuesta 1,3 UF/mes o 13 UF/año. El plan Enterprise (50+ usuarios) cuesta 2,8 UF/mes o 28 UF/año. Contratando el plan anual obtienes 2 meses gratis respecto al precio mensual. La equivalencia en CLP se calcula automáticamente con el valor de la UF del día.",
    },
    {
        q: "¿Por qué los precios están en UF y no en CLP?",
        a: "Expresar los precios en UF protege tanto al cliente como al proveedor de la variación inflacionaria. Así el valor real del servicio se mantiene estable en el tiempo. La equivalencia en pesos chilenos (CLP) se muestra siempre actualizada en la sección de planes, calculada con el valor oficial de la UF del día publicado por el Banco Central de Chile.",
    },
    {
        q: "¿Cuántos usuarios incluye cada plan?",
        a: "El plan Starter incluye hasta 3 usuarios simultáneos, ideal para equipos pequeños o empresas que recién comienzan a gestionar licitaciones. El plan Business incluye hasta 10 usuarios, pensado para equipos comerciales activos. El plan Enterprise incluye 50 o más usuarios, con soporte para múltiples divisiones bajo arquitectura multi-tenant. Todos los planes incluyen reglas de priorización ilimitadas y sincronización ChileCompra cada 15 minutos.",
    },
    {
        q: "¿Cuánto tarda en estar operativo el software de licitaciones?",
        a: "Entre 5 y 20 minutos. Creas tu cuenta en el SaaS licitaciones públicas, configuras 1 o 2 reglas y la primera sincronización ChileCompra API se ejecuta de inmediato. El asistente de onboarding integrado te guía paso a paso sin necesidad de soporte técnico.",
    },
    {
        q: "¿Puedo cambiar de plan si mi equipo crece?",
        a: "Sí. Puedes subir de plan en cualquier momento desde la configuración de tu cuenta. El cambio es inmediato y el cobro se prorratea según los días restantes del período activo. No hay penalizaciones por cambio de plan ni contratos de permanencia mínima.",
    },
    {
        q: "¿Mis datos están seguros en la plataforma licitaciones ChileCompra?",
        a: "Sí. Cada empresa proveedora opera en un tenant aislado (arquitectura multi-tenant), autenticamos vía JWT en cookies httpOnly + Secure (a prueba de XSS), y registramos todas las acciones sensibles en el log de auditoría. HTTPS forzado en producción.",
    },
    {
        q: "¿Convierten automáticamente UF a CLP?",
        a: "Sí. La conversión UF CLP automática consume mindicador.cl diariamente para cachear el valor de la UF y mostrar siempre el monto en CLP. Las reglas de priorización de licitaciones pueden expresarse en cualquiera de las dos unidades, y el sistema convierte automáticamente para comparar montos de forma consistente.",
    },
    {
        q: "¿Puedo integrar LicitX con mi CRM o ERP?",
        a: "Sí, el plan Enterprise incluye webhooks y endpoints públicos para integrar LicitX con CRMs (HubSpot, Salesforce, Pipedrive) y ERPs. La integración CRM ERP licitaciones permite sincronizar oportunidades del mercado público directamente con tu pipeline comercial. Si tienes un caso específico, escríbenos y lo priorizamos en el roadmap.",
    },
    {
        q: "¿LicitX envía alertas por WhatsApp?",
        a: "Sí. El plan Enterprise de LicitX incluye alertas configurables vía WhatsApp para notificar a tu equipo cuando se detecta una nueva licitación de interés, cuando una licitación está próxima a cerrar (15, 10 o 5 días) o cuando un documento corporativo está por vencer. Las alertas WhatsApp se configuran desde el panel de administración y complementan las notificaciones in-app y por email que están disponibles en todos los planes.",
    },
    {
        q: "¿Qué significa gestión integral del proceso de licitación?",
        a: "Significa que LicitX no es solo un detector de oportunidades: es una plataforma que acompaña a tu equipo en cada etapa del proceso licitatorio. Desde que se publica la licitación en ChileCompra hasta que se adjudica y se gestiona la Orden de Compra, LicitX centraliza la información, automatiza las alertas, genera la propuesta con IA, registra el historial de postulaciones y mide la tasa de adjudicación. Esto es lo que diferencia a LicitX de las plataformas que solo envían alertas por email.",
    },
    {
        q: "¿LicitX es solo para encontrar licitaciones o gestiona todo el proceso?",
        a: "LicitX es una plataforma de gestión integral del proceso de licitación pública. Cubre cada etapa: (1) detección automática de oportunidades en ChileCompra, Compra Ágil y Órdenes de Compra; (2) priorización con reglas personalizadas; (3) asignación de responsables y seguimiento por estados; (4) generación de propuesta comercial en Word con IA usando las bases; (5) gestión documental con alertas de renovación; (6) centralización de Órdenes de Compra; y (7) reportería para el comité comercial. El objetivo es administrar el ciclo de venta al Estado desde una operación única y trazable.",
    },
    {
        q: "¿LicitX puede generar propuestas comerciales automáticamente?",
        a: "Sí. LicitX incluye un módulo de análisis de bases con IA que lee íntegramente las bases técnicas y administrativas de la licitación, extrae automáticamente los requisitos obligatorios, criterios de evaluación y condiciones económicas, y genera un borrador de propuesta técnica y económica en formato Word. Puedes adjuntar el PDF de las bases, respuestas a preguntas o anexos para que la IA interrogue los documentos y adapte la propuesta al contexto específico de la licitación. El documento se genera con el branding de tu empresa y puede reformularse con instrucciones de voz.",
    },
    {
        q: "¿La IA de LicitX lee las bases extensas de la licitación?",
        a: "Sí. Esta es una de las capacidades más valoradas de LicitX: la IA lee íntegramente las bases técnicas y administrativas, incluyendo bases extensas de cientos de páginas, e identifica y extrae automáticamente los requisitos obligatorios, criterios de evaluación, ponderaciones, plazos de entrega y condiciones económicas. Adjunta el PDF de las bases directamente al módulo de propuestas para que la IA interrogue el documento y construya una propuesta alineada con los criterios reales de adjudicación. También puedes adjuntar aclaraciones, adendas o respuestas a preguntas para enriquecer el análisis.",
    },
    {
        q: "¿LicitX gestiona todo el proceso de postulación a licitaciones?",
        a: "Sí. LicitX gestiona todo el proceso de postulación desde una sola plataforma: detecta la oportunidad, permite marcarla como 'en preparación' al asignar un responsable, genera la propuesta con IA, registra la postulación con fecha y documentos adjuntos, y hace seguimiento hasta el resultado final (adjudicada o desierta). Cada acción queda registrada en el log de auditoría. El sistema calcula automáticamente tu tasa de adjudicación histórica y genera reportes trimestrales para el comité comercial.",
    },
    {
        q: "¿Cómo funciona el Score IA de adjudicación de LicitX?",
        a: "El Score IA de adjudicación de LicitX es un análisis predictivo personalizado que calcula la probabilidad de ganar cada licitación. El modelo considera el historial del organismo comprador (con qué frecuencia adjudica, a qué montos, a qué tipo de proveedor), el comportamiento de los competidores habituales en ese segmento, los montos adjudicados anteriores en licitaciones similares y el perfil configurado de tu empresa. El resultado es un score de probabilidad de adjudicación personalizado por empresa, no genérico, que cambia según las reglas y el historial de tu propia organización.",
    },
    {
        q: "¿LicitX recomienda a qué precio debo ofertar para ganar una licitación?",
        a: "LicitX entrega un rango de precio de referencia basado en adjudicaciones similares disponibles en ChileCompra. El sistema analiza montos pagados por organismos comparables y la distribución histórica de procesos equivalentes para aportar contexto a la oferta económica. La decisión final sigue correspondiendo al equipo, considerando costos, bases y estrategia comercial.",
    },
    {
        q: "¿LicitX analiza a los competidores que compiten en mis licitaciones?",
        a: "Sí. LicitX incluye análisis de competidores para cada licitación: identifica qué proveedores compiten habitualmente en tu segmento de mercado público, cuál es su tasa de adjudicación histórica, a qué precios suelen ganar y en qué organismos tienen mayor presencia. También puedes analizar las Órdenes de Compra de tus competidores para conocer su volumen de negocio con el Estado y detectar organismos donde no estén compitiendo activamente.",
    },
    {
        q: "¿LicitX permite analizar precios históricos de adjudicaciones en ChileCompra?",
        a: "Sí. LicitX analiza precios históricos de adjudicaciones disponibles en ChileCompra. Puedes consultar cuánto han pagado organismos compradores por servicios o productos similares, revisar montos por proceso y construir una referencia de precio basada en antecedentes del mercado público chileno. Esa información complementa el análisis de costos, las bases y la decisión comercial de tu equipo.",
    },
    {
        q: "¿Puedo ver el market share de mi rubro en el mercado público chileno?",
        a: "Sí. LicitX analiza el market share por rubro y organismo comprador en el mercado público chileno: quién gana más licitaciones en tu segmento, con qué frecuencia, a qué precio promedio y en qué organismos. Esta inteligencia de mercado te permite identificar nichos donde la competencia es menor, detectar organismos compradores con alta frecuencia de adjudicación en tu rubro y priorizar las oportunidades con mayor probabilidad de éxito para tu empresa.",
    },
    {
        q: "¿LicitX muestra las Órdenes de Compra de mis competidores?",
        a: "Sí. LicitX permite analizar las Órdenes de Compra de tus competidores en el mercado público chileno: cuántas OC han recibido, de qué organismos, a qué montos y en qué rubros. Esta información es pública en ChileCompra y LicitX la centraliza y visualiza para que puedas entender el volumen de negocio de tus competidores con el Estado, detectar organismos donde no estén compitiendo y construir una estrategia de penetración de mercado público basada en datos reales.",
    },
    {
        q: "¿Cómo gestiona LicitX las Órdenes de Compra y Compra Ágil?",
        a: "LicitX sincroniza automáticamente cada 15 minutos las Órdenes de Compra y Compra Ágil desde la API oficial de ChileCompra. Puedes ver el estado de cada OC, filtrar por estado (aceptada, enviada, recepcionada, etc.) y tener un registro centralizado de todas las compras ágiles adjudicadas a tu empresa, sin necesidad de revisar manualmente el portal de Mercado Público.",
    },
    {
        q: "¿LicitX tiene inteligencia comercial avanzada de competencia o es solo un sistema de alertas?",
        a: "LicitX combina alertas con inteligencia comercial: (1) precios históricos de adjudicaciones en ChileCompra; (2) Órdenes de Compra de proveedores del mismo nicho para conocer organismos y montos; (3) market share por rubro y comprador; y (4) un Score IA que aporta contexto del organismo, proveedores adjudicados y referencias de precio. Así, la alerta se convierte en una decisión documentada y no en un correo aislado.",
    },
    {
        q: "¿Puedo saber exactamente a qué precio adjudicó mi competidor directo el año pasado?",
        a: "Sí. Cuando los datos están disponibles en ChileCompra, LicitX permite revisar adjudicaciones y Órdenes de Compra de proveedores específicos: procesos, organismos, montos y rubros. El análisis histórico entrega referencias observables para comparar el mercado y preparar la estrategia comercial de tu empresa.",
    },
];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */
const formatClp = (n) => `$${n.toLocaleString("es-CL")}`;
const formatUf = (n) => `${n.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} UF`;
const annualDiscount = (monthly, annual) =>
    Math.round((1 - annual / (monthly * 12)) * 100);

/* -------------------------------------------------------------------------- */
/* Modal de contratación                                                       */
/* -------------------------------------------------------------------------- */
const ContractModal = ({ plan, billing, onClose }) => {
    const [form, setForm] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    const priceUf = billing === "monthly" ? plan.price_monthly_uf : plan.price_annual_uf;
    const priceClp = billing === "monthly" ? plan.price_monthly : plan.price_annual;
    const billingLabel = billing === "monthly" ? "Mensual" : "Anual";

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.company_name || !form.contact_name || !form.contact_email) {
            toast.error("Completa los campos obligatorios");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${BACKEND_URL}/api/plans/contract`, {
                plan: plan.key,
                billing,
                ...form,
            });
            setDone(true);
            navigate(`/gracias/${plan.key}?origen=landing-plan&ciclo=${billing}`);
        } catch (err) {
            toast.error("Error al enviar la solicitud. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,10,20,0.85)" }}>
            <div className="relative w-full max-w-md rounded-2xl border border-[#22D3EE]/20 bg-[#0F1620] shadow-2xl p-5 sm:p-8 max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
                    <X className="h-5 w-5" />
                </button>

                {done ? (
                    <div className="text-center py-6">
                        <div className="h-14 w-14 rounded-full bg-[#22D3EE]/10 border border-[#22D3EE]/30 grid place-items-center mx-auto mb-4">
                            <CheckCircle2 className="h-7 w-7 text-[#22D3EE]" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-white mb-2">¡Solicitud enviada!</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">
                            Nuestro equipo revisará tu solicitud para el plan <strong className="text-white">{plan.name} {billingLabel}</strong> y se pondrá en contacto contigo a la brevedad.
                        </p>
                        <Button onClick={onClose} className="mt-6 bg-[#22D3EE] hover:bg-[#06B6D4] text-[#050A14] font-semibold w-full">
                            Cerrar
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="mb-6">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#22D3EE] font-semibold mb-1">Contratar plan</div>
                            <h3 className="font-display text-xl font-bold text-white">
                                {plan.name} — {billingLabel}
                            </h3>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-2xl font-bold text-[#22D3EE]">{formatUf(priceUf)}</span>
                                <span className="text-slate-400 text-sm">/ {billing === "monthly" ? "mes" : "año"}</span>
                                {billing === "annual" && (
                                    <span className="text-xs text-emerald-400 font-semibold">2 meses gratis</span>
                                )}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">≈ {formatClp(priceClp)} + IVA</div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label className="text-slate-300 text-sm">Nombre de la empresa *</Label>
                                <Input
                                    className="mt-1 bg-[#0B1220] border-[#1F2A37] text-white placeholder:text-slate-600 focus:border-[#22D3EE]"
                                    placeholder="InnovaX SpA"
                                    value={form.company_name}
                                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300 text-sm">Nombre del contacto *</Label>
                                <Input
                                    className="mt-1 bg-[#0B1220] border-[#1F2A37] text-white placeholder:text-slate-600 focus:border-[#22D3EE]"
                                    placeholder="Felipe Penailillo"
                                    value={form.contact_name}
                                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300 text-sm">Email de contacto *</Label>
                                <Input
                                    type="email"
                                    className="mt-1 bg-[#0B1220] border-[#1F2A37] text-white placeholder:text-slate-600 focus:border-[#22D3EE]"
                                    placeholder="contacto@empresa.cl"
                                    value={form.contact_email}
                                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-slate-300 text-sm">Teléfono (opcional)</Label>
                                <Input
                                    className="mt-1 bg-[#0B1220] border-[#1F2A37] text-white placeholder:text-slate-600 focus:border-[#22D3EE]"
                                    placeholder="+56 9 1234 5678"
                                    value={form.contact_phone}
                                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#050A14] font-semibold mt-2"
                            >
                                {loading ? "Enviando…" : "Enviar solicitud"}
                                {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

const TopBar = () => {
    const { theme, toggleTheme } = useTheme();
    return (
        <header className="sticky top-0 z-30 border-b border-white/10 bg-[#050A14]/90 backdrop-blur">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo-link">
                    <img
                        src={LOGO_URL}
                        alt="LicitX - Software de licitaciones ChileCompra"
                        className="h-8 md:h-9"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                </Link>
                <nav className="hidden xl:flex items-center gap-5 text-sm font-medium text-slate-300">
                    <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
                    <a href="#como-funciona" className="hover:text-white transition-colors">Cómo funciona</a>
                    <a href="#beneficios" className="hover:text-white transition-colors">Beneficios</a>
                    <a href="#planes" className="hover:text-white transition-colors text-[#22D3EE] font-semibold">Planes</a>
                    <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
                    <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                    <Link to="/videos" className="hover:text-white transition-colors">Videos</Link>
                    <Link to="/mcp" className="hover:text-white transition-colors">MCP</Link>
                    <Link to="/contacto" className="hover:text-white transition-colors">Contacto</Link>
                </nav>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        aria-label="Cambiar tema"
                        data-testid="landing-theme-toggle"
                        className="text-slate-300 hover:text-white hover:bg-white/10"
                    >
                        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                    </Button>
                    <Button
                        asChild
                        variant="ghost"
                        className="hidden sm:inline-flex text-slate-300 hover:text-white hover:bg-white/10"
                        data-testid="landing-login-link"
                    >
                        <Link to="/acceso">Iniciar sesión</Link>
                    </Button>
                    <Button
                        asChild
                        data-testid="landing-cta-primary"
                        className="bg-[#22D3EE] hover:bg-[#06B6D4] text-[#050A14] font-semibold"
                    >
                        <a href="#planes">
                            Ver planes
                            <ArrowRight className="h-4 w-4 ml-1.5" />
                        </a>
                    </Button>
                </div>
            </div>
        </header>
    );
};

const Hero = () => (
    <section className="relative overflow-hidden procurex-hero">
        <div aria-hidden="true" className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
             style={{ background: "radial-gradient(circle, #22D3EE, transparent 70%)" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-32 pb-20 md:pb-32 text-center relative z-10">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-[#22D3EE] font-semibold border border-[#22D3EE]/30 bg-[#22D3EE]/10 rounded-full px-3 py-1 whitespace-nowrap">
                <Sparkles className="h-3.5 w-3.5 shrink-0" /> Plataforma SaaS · Licitaciones Chile
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mt-6 leading-[1.05] text-white max-w-4xl mx-auto">
                Software de licitaciones públicas para detectar antes.{" "}
                <span className="text-[#22D3EE]">Postula con evidencia.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mt-6 max-w-2xl mx-auto leading-relaxed">
                LicitX ayuda a proveedores del Estado a buscar licitaciones, Compras Ágiles y Órdenes de Compra en ChileCompra. Prioriza oportunidades según tu nicho, anticipa renovaciones, compara precios y competidores, y acompaña a tu equipo desde la alerta hasta la adjudicación. <strong className="text-white">Cada decisión conserva contexto comercial y trazabilidad.</strong>
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Button
                    asChild
                    size="lg"
                    className="text-base bg-[#22D3EE] hover:bg-[#06B6D4] text-[#050A14] font-semibold"
                    data-testid="hero-cta-primary"
                >
                    <Link to="/contacto">
                        Encontrar oportunidades para mi empresa <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                </Button>
                <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="text-base border-white/20 text-white hover:bg-white/10 hover:text-white"
                    data-testid="hero-cta-secondary"
                >
                    <a href="#planes">
                        Ver planes y capacidades <ArrowRight className="h-4 w-4 ml-2" />
                    </a>
                </Button>
                <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="text-base border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE]/10 hover:text-[#22D3EE] hover:border-[#22D3EE]"
                    data-testid="hero-cta-contact"
                >
                    <Link to="/contacto">
                        ¿Tienes dudas? Contáctanos
                    </Link>
                </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-[#22D3EE]" /> Primeras coincidencias guiadas en menos de 10 minutos</div>
                <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#22D3EE]" /> Recomendaciones explicables, no una caja negra</div>
                <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#22D3EE]" /> Trazabilidad de reglas y decisiones comerciales</div>
                <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#22D3EE]" /> Competencia y precios históricos para decidir</div>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                {[
                    { k: "Encontrar", v: "Oportunidades según tu nicho y regiones" },
                    { k: "Priorizar", v: "Alta y media con reglas auditables" },
                    { k: "Preparar", v: "Bases, documentos y propuesta con IA" },
                    { k: "Aprender", v: "Feedback, competencia y resultados reales" },
                ].map((s) => (
                    <div key={s.k} className="border border-[#22D3EE]/20 bg-black/30 backdrop-blur rounded-xl p-4">
                        <div className="font-display text-sm font-bold text-[#22D3EE]">{s.k}</div>
                        <div className="text-xs text-slate-400 mt-1">{s.v}</div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const DifferentiatorsSection = () => (
    <section id="por-que-licitx" className="border-b border-slate-200 bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
                <div>
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Decidir mejor en Mercado Público</span>
                    <h2 className="mt-3 font-display text-3xl font-bold text-slate-950 md:text-4xl">
                        Más que alertas de licitaciones: contexto para encontrar, evaluar y gestionar oportunidades.
                    </h2>
                </div>
                <p className="max-w-3xl text-base leading-7 text-slate-600 lg:justify-self-end">
                    LicitX reúne búsqueda de licitaciones públicas, Compra Ágil, Órdenes de Compra, análisis de competidores, precios históricos y preparación documental. Así, proveedores del Estado pueden priorizar por su nicho, anticipar renovaciones y sostener cada decisión con datos verificables.
                </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {DIFFERENTIATORS.map((item) => {
                    const Icon = item.icon;
                    return (
                        <article key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm transition-colors hover:border-emerald-300">
                            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-100 text-emerald-700">
                                <Icon className="h-5 w-5" />
                            </div>
                            <h3 className="mt-5 text-lg font-bold text-slate-950">{item.title}</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-600">{item.body}</p>
                        </article>
                    );
                })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button asChild className="bg-emerald-600 font-semibold text-white hover:bg-emerald-700">
                    <Link to="/contacto">Solicitar una demostración <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Link to="/videos" className="text-sm font-semibold text-emerald-800 hover:text-emerald-950">Ver cómo funciona LicitX →</Link>
                <Link to="/blog" className="text-sm font-semibold text-emerald-800 hover:text-emerald-950">Leer guías para vender al Estado →</Link>
            </div>
        </div>
    </section>
);

const FeaturesSection = () => (
    <section id="features" className="py-20 md:py-28 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #050A14 0%, #0B1220 60%, #0A1A2F 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
                <span className="text-xs uppercase tracking-[0.18em] text-[#22D3EE] font-semibold">Funcionalidades</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-white">
                    Gestión de licitaciones, inteligencia de mercado y propuestas con IA en una sola plataforma.
                </h2>
                <p className="text-slate-400 mt-4 leading-relaxed">
                    Centraliza alertas de licitaciones y Mercado Público, reglas de prioridad, seguimiento de postulaciones, documentos y post-adjudicación. Para evaluar mejor cada proceso, incorpora <strong className="text-white">precios históricos de adjudicación</strong>, Órdenes de Compra, análisis por organismo y proveedor, posicionamiento por RUT y un Score IA explicable. No se limita a enviar oportunidades: ayuda a convertirlas en decisiones y tareas gestionables.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
                {FEATURES.map((f) => {
                    const Icon = f.icon;
                    return (
                        <Card key={f.title} className="bg-white/5 border-white/10 hover:border-[#22D3EE]/40 transition-colors">
                            <CardContent className="p-6">
                                <div className="h-10 w-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] grid place-items-center mb-4">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-lg font-semibold text-white">{f.title}</h3>
                                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.body}</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    </section>
);

const HowItWorks = () => (
    <section id="como-funciona" className="py-20 md:py-28 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #050A14 0%, #0B1220 60%, #0A1A2F 100%)" }}>
        <div aria-hidden="true" className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-10 pointer-events-none"
             style={{ background: "radial-gradient(circle, #22D3EE, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-2xl">
                <span className="text-xs uppercase tracking-[0.18em] text-[#22D3EE] font-semibold">Cómo funciona</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-white">
                    En menos de 10 minutos, tu equipo puede pasar de configurar su foco a revisar oportunidades reales.
                </h2>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                {STEPS.map((s) => (
                    <li key={s.n} className="rounded-2xl border border-[#22D3EE]/20 bg-black/30 backdrop-blur p-6">
                        <div className="font-display text-xs font-bold text-[#22D3EE] tracking-widest">{s.n}</div>
                        <h3 className="font-display text-lg font-semibold mt-3 text-white">{s.title}</h3>
                        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.body}</p>
                    </li>
                ))}
            </ol>
        </div>
    </section>
);

const BenefitsSection = () => (
    <section id="beneficios" className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
                <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Beneficios</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-foreground">
                    Resultados comerciales que tu equipo puede medir, no una lista más de alertas.
                </h2>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                    LicitX acompaña el avance desde el primer ingreso: reglas configuradas, oportunidades relevantes revisadas, análisis ejecutados, propuestas generadas y postulaciones gestionadas. Así, cada equipo puede ordenar su operación y concentrarse en las oportunidades con mayor ajuste comercial.
                </p>
                <ul className="mt-8 space-y-3">
                    {BENEFITS.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm leading-relaxed">
                            <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                            <span className="text-foreground">{b}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="relative">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-xl">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Panel de resultados</div>
                    <div className="font-display text-xl font-semibold mt-2 text-foreground">Sabrás si LicitX está generando valor</div>
                    <div className="grid grid-cols-2 gap-3 mt-5">
                        {[
                            ["Relevancia", "Qué oportunidades se ajustan mejor a tus reglas"],
                            ["Prioridad", "Qué procesos requieren atención primero"],
                            ["Propuestas", "Qué documentos comerciales están en preparación"],
                            ["Postulaciones", "Cómo avanza cada oportunidad gestionada"],
                        ].map(([title, body]) => (
                            <div key={title} className="rounded-xl border border-border bg-muted/30 p-3">
                                <div className="text-sm font-semibold text-primary">{title}</div>
                                <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">
                        Desde el onboarding hasta la postulación, cada avance queda visible y auditable para administración y gerencia.
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* -------------------------------------------------------------------------- */
/* Sección Asistente IA                                                        */
/* -------------------------------------------------------------------------- */
const CAPACIDADES_IA = [
    {
        icon: "📄",
        titulo: "Entiende las bases en segundos",
        desc: "Pregúntale sobre cualquier licitación y obtendrás un resumen ejecutivo claro: qué pide el organismo, qué entregar y si conviene postular."
    },
    {
        icon: "📅",
        titulo: "Fechas clave al instante",
        desc: "Consulta las fechas críticas de cierre, apertura técnica, apertura económica y adjudicación de cualquier licitación activa en tu cuenta."
    },
    {
        icon: "🎯",
        titulo: "Criterios de evaluación explicados",
        desc: "Entiende cómo te va a evaluar el Estado y qué aspectos de tu oferta reforzar para maximizar tu puntaje en cada criterio."
    },
    {
        icon: "💡",
        titulo: "Asesoría estratégica personalizada",
        desc: "Recibe referencias de precio, riesgos detectados y próximos pasos para preparar una oferta alineada con las bases."
    },
    {
        icon: "🔍",
        titulo: "Palabras clave sugeridas para tu empresa",
        desc: "Basado en tu historial comercial y adjudicaciones, sugiere keywords específicas para que no te pierdas ninguna oportunidad relevante."
    },
    {
        icon: "📊",
        titulo: "Análisis de tu posición en el mercado",
        desc: "Explica en lenguaje natural cuánto del mercado estás abarcando, en qué rubros tienes más oportunidades y cómo mejorar tu tasa de adjudicación."
    },
];

const AsistenteIASection = () => (
    <section id="asistente-ia" className="py-20 md:py-28 relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--primary)/0.04) 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
                <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Inteligencia Artificial Conversacional</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-foreground">
                    Habla con tus licitaciones.<br />
                    <span className="text-primary">Obtén respuestas en segundos.</span>
                </h2>
                <p className="text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
                    LicitX incluye un <strong className="text-foreground">asistente de inteligencia artificial conversacional</strong> que analiza cada licitación en profundidad. Pregúntale sobre las bases, los criterios de evaluación, las fechas clave o pídele una estrategia de precio — responde con datos reales de ChileCompra, no con respuestas genéricas.
                </p>
                <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
                    El asistente conecta la licitación activa con <strong className="text-foreground">bases, criterios, precios históricos, organismos y proveedores adjudicados</strong>. Así puedes consultar antecedentes del mismo nicho y preparar una decisión sustentada, directamente dentro de la plataforma.
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {CAPACIDADES_IA.map((cap) => (
                    <div key={cap.titulo} className="rounded-2xl border border-border bg-card p-6 hover:border-primary/30 transition-colors">
                        <div className="text-3xl mb-3">{cap.icon}</div>
                        <h3 className="font-display font-semibold text-foreground text-base mb-2">{cap.titulo}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{cap.desc}</p>
                    </div>
                ))}
            </div>
            <div className="mt-12 rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 max-w-3xl mx-auto">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-lg">✨</span>
                    </div>
                    <div>
                        <p className="font-semibold text-foreground">Análisis profundo, no alertas superficiales</p>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            Cada respuesta parte de la licitación que tienes abierta: nombre, organismo, ítems, fechas, criterios de evaluación y antecedentes disponibles del mercado. El asistente conecta <strong className="text-foreground">precios históricos de adjudicación, Órdenes de Compra, organismos y competidores de tu nicho</strong> para entregar contexto comercial trazable, no respuestas genéricas.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);
/* -------------------------------------------------------------------------- */
/* Sección de Planes                                                           */
/* -------------------------------------------------------------------------- */
const PlansSection = () => {
    const [billing, setBilling] = useState("monthly");
    const [selected, setSelected] = useState(null); // { plan, billing }

    return (
        <>
            {selected && (
                <ContractModal
                    plan={selected.plan}
                    billing={selected.billing}
                    onClose={() => setSelected(null)}
                />
            )}

            <section id="planes" className="scroll-mt-24 py-20 md:py-28 relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 60%, #EFFAF8 100%)" }}>
                <div aria-hidden="true" className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-20 pointer-events-none"
                     style={{ background: "radial-gradient(circle, #99F6E4, transparent 70%)" }} />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-10">
                        <span className="text-xs uppercase tracking-[0.18em] text-[#0E6E5A] font-semibold">Planes y precios</span>
                        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-[#0A2540]">
                            Planes de software de licitaciones Chile para cada equipo.
                        </h2>
                        <p className="text-slate-600 mt-4 max-w-xl mx-auto leading-relaxed">
                            Sin costos ocultos. Todos los planes incluyen sincronización con ChileCompra, reglas ilimitadas y soporte.
                        </p>

                        {/* Toggle mensual / anual */}
                        <div className="inline-flex items-center gap-1 mt-6 rounded-full border border-[#BDE8DD] bg-white p-1 shadow-sm">
                            <button
                                onClick={() => setBilling("monthly")}
                                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    billing === "monthly"
                                        ? "bg-[#0E9F6E] text-white font-semibold"
                                        : "text-slate-500 hover:text-[#0A2540]"
                                }`}
                            >
                                Mensual
                            </button>
                            <button
                                onClick={() => setBilling("annual")}
                                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                    billing === "annual"
                                        ? "bg-[#0E9F6E] text-white font-semibold"
                                        : "text-slate-500 hover:text-[#0A2540]"
                                }`}
                            >
                                Anual
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                    billing === "annual" ? "bg-white/30 text-white" : "bg-emerald-500/15 text-emerald-700"
                                }`}>
                                    2 meses gratis
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PLANS_DATA.map((plan) => {
                            const Icon = plan.icon;
                            const price = billing === "monthly" ? plan.price_monthly : plan.price_annual;
                            const discount = annualDiscount(plan.price_monthly, plan.price_annual);

                            return (
                                <div
                                    key={plan.key}
                                    className={`relative rounded-2xl p-5 sm:p-8 flex flex-col transition-all ${
                                        plan.highlight
                                            ? "border-2 border-[#0E9F6E] bg-white shadow-[0_12px_32px_rgba(14,159,110,0.16)]"
                                            : "border border-[#DCE9E7] bg-white shadow-sm"
                                    }`}
                                >
                                    {plan.highlight && (
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#22D3EE] text-[#050A14] text-[11px] font-bold uppercase tracking-widest rounded-full whitespace-nowrap">
                                            Más popular
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-10 w-10 rounded-xl bg-[#22D3EE]/10 border border-[#22D3EE]/20 grid place-items-center">
                                            <Icon className="h-5 w-5 text-[#22D3EE]" />
                                        </div>
                                        <div>
                                            <div className="font-display text-lg font-bold text-[#0A2540]">{plan.name}</div>
                                            <div className="text-xs text-slate-500">{plan.users}</div>
                                        </div>
                                    </div>

                                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{plan.description}</p>

                                    <div className="mb-6">
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-display text-3xl font-bold text-[#0A2540]">
                                                {billing === "monthly" ? formatUf(plan.price_monthly_uf) : formatUf(plan.price_annual_uf)}
                                            </span>
                                            <span className="text-slate-500 text-sm">/ {billing === "monthly" ? "mes" : "año"}</span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {billing === "monthly"
                                                ? `≈ ${formatClp(plan.price_monthly)} / mes + IVA`
                                                : `≈ ${formatClp(plan.price_annual)} / año + IVA`}
                                        </div>
                                        {billing === "annual" && (
                                            <div className="text-xs text-emerald-400 font-semibold mt-1">
                                                Ahorra {formatUf(plan.price_monthly_uf * 2)} al año — 2 meses gratis
                                            </div>
                                        )}
                                        {billing === "monthly" && (
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                o {formatUf(plan.price_annual_uf)}/año con descuento anual
                                            </div>
                                        )}
                                    </div>

                                    <ul className="space-y-2.5 mb-4 flex-1">
                                        {plan.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                <CheckCircle2 className="h-4 w-4 text-[#22D3EE] shrink-0 mt-0.5" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    {plan.locked && plan.locked.length > 0 && (
                                        <ul className="space-y-2 mb-8 border-t border-slate-100 pt-3">
                                            {plan.locked.map((f) => (
                                                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-400">
                                                    <Lock className="h-4 w-4 text-slate-700 shrink-0 mt-0.5" />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {(!plan.locked || plan.locked.length === 0) && <div className="mb-8" />}

                                    <button
                                        onClick={() => setSelected({ plan, billing })}
                                        className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                                            plan.highlight
                                                ? "bg-[#0E9F6E] hover:bg-[#087A55] text-white"
                                                : "border border-[#0E9F6E]/40 text-[#0E9F6E] hover:bg-[#0E9F6E]/10"
                                        }`}
                                    >
                                        Contratar {plan.name}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                            <span className="text-sm text-emerald-400 font-semibold">14 días de prueba gratuita — sin tarjeta de crédito</span>
                        </div>
                    </div>
                    <p className="text-center text-xs text-slate-500 mt-4">
                        Precios en UF + IVA. Equivalencia en CLP calculada con el valor de la UF del día. ¿Necesitas un plan personalizado?{" "}
                        <a href="mailto:hola@licitx.cl" className="text-[#0E6E5A] font-semibold hover:underline">Contáctanos</a>.
                    </p>
                </div>
            </section>
        </>
    );
};

const FaqSection = () => (
    <section id="faq" className="scroll-mt-24 py-20 md:py-28 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
                <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Preguntas frecuentes</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-foreground">
                    Preguntas frecuentes sobre la plataforma licitaciones ChileCompra.
                </h2>
            </div>
            <Accordion type="single" collapsible className="mt-8" data-testid="landing-faq">
                {FAQ.map((f, i) => (
                    <AccordionItem key={f.q} value={`item-${i}`}>
                        <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
                        <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    </section>
);

const FinalCta = () => (
    <section className="py-20 md:py-28 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #050A14 0%, #0B1220 60%, #0A1A2F 100%)" }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-20"
                 style={{ background: "radial-gradient(circle, #22D3EE, transparent 70%)" }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="rounded-3xl border border-[#22D3EE]/20 bg-black/30 backdrop-blur text-white p-6 sm:p-10 md:p-14 text-center shadow-xl">
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight text-white">
                    Activa el software de licitaciones Chile en tu empresa hoy.
                </h2>
                <p className="text-slate-300 mt-4 max-w-xl mx-auto leading-relaxed">
                    La próxima licitación pública importante para tu negocio se publicará en ChileCompra en menos de 24 horas. Mejor que llegue directo a tu equipo con alertas de licitaciones públicas en tiempo real.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                    <Button
                        asChild
                        size="lg"
                        className="text-base bg-[#22D3EE] hover:bg-[#06B6D4] text-[#050A14] font-semibold"
                        data-testid="cta-final-primary"
                    >
                        <a href="#planes">
                            Ver planes y precios <ArrowRight className="h-4 w-4 ml-2" />
                        </a>
                    </Button>
                    <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="text-base border-white/20 text-white hover:bg-white/10 hover:text-white"
                    >
                        <Link to="/acceso">
                            Acceder a la plataforma
                        </Link>
                    </Button>
                </div>
            </div>
        </div>
    </section>
);

const Footer = () => (
    <footer className="border-t border-white/10"
        style={{ background: "linear-gradient(135deg, #050A14 0%, #0B1220 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
            {/* Logo + descripción — fila completa en todos los tamaños */}
            <div className="mb-8">
                <img
                    src={LOGO_URL}
                    alt="LicitX - Plataforma licitaciones ChileCompra"
                    className="h-7"
                    style={{ filter: "brightness(0) invert(1)" }}
                />
                <p className="text-sm text-slate-400 mt-3 leading-relaxed max-w-lg">
                    Software de licitaciones públicas en Chile con monitoreo de ChileCompra, análisis de precios históricos, Órdenes de Compra, proveedores adjudicados y Score IA explicable.
                </p>
            </div>

            {/* Columnas de navegación — 2 cols en móvil, 3 en desktop */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Plataforma</div>
                    <ul className="space-y-2 text-sm">
                        <li><a href="#features" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Funcionalidades</a></li>
                        <li><a href="#como-funciona" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Cómo funciona</a></li>
                        <li><a href="#planes" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Planes</a></li>
                        <li><a href="#faq" className="text-slate-300 hover:text-[#22D3EE] transition-colors">FAQ</a></li>
                    </ul>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Recursos</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/blog" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Blog</Link></li>
                        <li><Link to="/videos" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Videoteca</Link></li>
                        <li><Link to="/mcp" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Integración MCP</Link></li>
                        <li><Link to="/contacto" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Contacto</Link></li>
                        <li><Link to="/acceso" className="text-slate-300 hover:text-[#22D3EE] transition-colors">Iniciar sesión</Link></li>
                    </ul>
                </div>
                <div className="col-span-2 md:col-span-1">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Empresa</div>
                    <ul className="space-y-2 text-sm">
                        <li><span className="text-slate-300">Santiago, Chile</span></li>
                        <li>
                            <a href="mailto:hola@licitx.cl" className="text-slate-300 hover:text-[#22D3EE] transition-colors">
                                hola@licitx.cl
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10 mt-8 pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span>© {new Date().getFullYear()} LicitX. Todos los derechos reservados.</span>
                    <a
                        href="https://innovax.cl"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LicitX, desarrollado por InnovaX. Visitar innovax.cl"
                        className="group inline-flex items-center gap-1.5 rounded-md -my-1 px-1.5 py-1 text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#22D3EE]"
                    >
                        <span className="whitespace-nowrap">by</span>
                        <img
                            src={INNOVAX_LOGO_URL}
                            alt="InnovaX"
                            className="h-4 sm:h-5 w-auto max-w-[104px] object-contain opacity-75 transition-opacity group-hover:opacity-100"
                        />
                    </a>
                </div>
                <span className="hidden sm:inline">Plataforma SaaS · ChileCompra · Made in Chile</span>
            </div>
        </div>
    </footer>
);

/* -------------------------------------------------------------------------- */
/* Blog preview section (últimas 3 entradas)                                  */
/* -------------------------------------------------------------------------- */
const BlogPreviewSection = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API_BASE}/api/blog/public/posts?page=1&per_page=3`)
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(d => setPosts(d.posts || []))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading || posts.length === 0) return null;

    const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" }) : "";

    return (
        <section className="py-20 border-t border-white/10" style={{ background: "linear-gradient(135deg, #050A14 0%, #0B1220 100%)" }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-[#22D3EE] font-semibold mb-2">Blog</p>
                        <h2 className="text-3xl font-bold text-white">Últimas publicaciones</h2>
                        <p className="text-slate-400 mt-2">Guías, estrategias y análisis para ganar más licitaciones.</p>
                    </div>
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors whitespace-nowrap"
                    >
                        Ver todo el blog →
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map(post => (
                        <Link
                            key={post.id}
                            to={`/blog/${post.slug}`}
                            className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl overflow-hidden transition-all duration-200 flex flex-col"
                        >
                            <div className="h-44 bg-gradient-to-br from-[#0A2540] to-[#0E6E5A] overflow-hidden shrink-0">
                                {post.cover_image_url ? (
                                    <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <span className="text-3xl font-bold text-white/20">LicitX</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-5 flex flex-col flex-1">
                                {post.published_at && (
                                    <p className="text-xs text-slate-500 mb-2">{fmtDate(post.published_at)}</p>
                                )}
                                <h3 className="text-base font-bold text-white mb-2 line-clamp-2 group-hover:text-[#22D3EE] transition-colors">
                                    {post.title}
                                </h3>
                                {post.excerpt && (
                                    <p className="text-sm text-slate-400 line-clamp-3 flex-1">{post.excerpt}</p>
                                )}
                                <div className="mt-4 flex items-center text-[#22D3EE] text-sm font-medium">
                                    Leer artículo <span className="ml-1 group-hover:translate-x-1 transition-transform inline-block">→</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/* Public landing page                                                        */
/* -------------------------------------------------------------------------- */
const LandingPage = () => {
    const location = useLocation();

    useEffect(() => {
        if (!location.hash) return undefined;
        const targetId = decodeURIComponent(location.hash.slice(1));
        if (!targetId) return undefined;
        let attempts = 0;
        let timer;
        const scrollToTarget = () => {
            const element = document.getElementById(targetId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            attempts += 1;
            if (attempts < 12) timer = window.setTimeout(scrollToTarget, 80);
        };
        timer = window.setTimeout(scrollToTarget, 0);
        return () => window.clearTimeout(timer);
    }, [location.hash, location.key]);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <PublicSeo
                title="Software de licitaciones públicas y ChileCompra | LicitX"
                description="Detecta licitaciones y Compras Ágiles, anticipa renovaciones, analiza competencia y prepara propuestas para vender al Estado con LicitX."
                path="/"
                imageAlt="LicitX, software de licitaciones públicas y ChileCompra"
                schemas={buildHomeSchemas(FAQ)}
            />
            <TopBar />
            <main data-testid="landing-page">
                <Hero />
                <DifferentiatorsSection />
                <PlansSection />
                <FeaturesSection />
                <HowItWorks />
                <BenefitsSection />
                <AsistenteIASection />
                <FaqSection />
                <FinalCta />
                <BlogPreviewSection />
            </main>
            <Footer />
        </div>
    );
};

export default LandingPage;
